import { getIssuePriority } from 'shared/constants/issues';
import intl from 'react-intl-universal';
import localData from 'utils/localData';
import { USER_DATA } from 'shared/constants/users';
import { formatDateTime } from 'utils/formatTime';
import logger from 'utils/logger';
import { checkUndefinedOrNull } from 'utils/misc';

const log = logger.createLogger('CSV Content');

//! Need to be called as a function as intl runs on runtime
const getBillingCSVHeader = (isKenshin) => {
  return isKenshin
    ? [
        intl.get('billing_csv_label_column_organization'),
        intl.get('billing_csv_label_column_issue_id'),
        intl.get('billing_csv_label_column_tag'),
        intl.get('billing_csv_label_column_created_at'),
        intl.get('billing_csv_label_column_done_at'),
        intl.get('billing_csv_label_column_original_fee'),
        intl.get('billing_csv_label_column_current_fee'),
        intl.get('billing_csv_label_column_remark'),
      ]
    : [
        intl.get('billing_csv_label_column_organization'),
        intl.get('billing_csv_label_column_patient_id'),
        intl.get('billing_csv_label_column_issue_id'),
        intl.get('billing_csv_label_column_emergency'),
        intl.get('billing_csv_label_column_tag'),
        intl.get('billing_csv_label_column_created_at'),
        intl.get('billing_csv_label_column_done_at'),
        intl.get('billing_csv_label_column_original_fee'),
        intl.get('billing_csv_label_column_current_fee'),
        intl.get('billing_csv_label_column_remark'),
      ];
};

const BILL_CHANGE_HISTORY_TYPE = {
  DEFAULT: 'default',
  RULE: 'rule',
  ADJUST: 'adjust',
};

export const createBillingCSVContent = (data, isReceived) => {
  const header = getBillingCSVHeader(false);
  const csvContent =
    'data:text/csv;charset=utf-8,' +
    '\uFEFF' +
    header.join(',') +
    '\n' +
    data
      .map((bill) => {
        const organizationName = isReceived
          ? bill.srcOrg.name
          : bill.dstOrg.name;
        const issueTitle = bill.issueTitle;
        const emergencyText = getIssuePriority(bill.issuePriority);
        const createdAt = formatDateTime(bill.issueCreatedAt);
        const doneAt = formatDateTime(bill.createdAt);
        const originalFee = bill.originalFee;
        const currentFee =
          bill.currentFee === null ? bill.originalFee : bill.currentFee;
        const patientName = checkUndefinedOrNull(
          bill.issue ? bill.issue.patientName : ''
        );

        let remarks = '';
        //! The change history is added already from the tags when the bill is created
        const changeHistory = JSON.parse(bill.changeHistory);
        if (changeHistory) {
          changeHistory.forEach((history) => {
            const historyType = history.type;

            if (historyType === BILL_CHANGE_HISTORY_TYPE.RULE) {
              const remark = history.remark;
              remarks += remark;
            } else if (historyType === BILL_CHANGE_HISTORY_TYPE.ADJUST) {
              const remark = `${intl.get('billing_csv_price_adjustment')} ${
                history.feeChange
              }`;
              remarks += remark;
            }
          });
        }
        const tags = JSON.parse(bill.tags);

        let tagLabels;
        if (tags) {
          tagLabels = tags
            .map((tag) => obtainTagLabelText(tag))
            .join(';')
            .replace(/,/g, ' ');
        }
        const remarks_ = remarks.replace(/,/g, ' ');
        return (
          organizationName +
          ',' +
          patientName +
          ',' +
          issueTitle +
          ',' +
          emergencyText +
          ',' +
          tagLabels +
          ',' +
          createdAt +
          ',' +
          doneAt +
          ',' +
          originalFee +
          ',' +
          currentFee +
          ',' +
          remarks_
        );
      })
      .join('\n');
  return csvContent;
};

export const createCSVKenshinCSVContent = (data, isReceived) => {
  const header = getBillingCSVHeader(true);

  const csvContent =
    'data:text/csv;charset=utf-8,' +
    '\uFEFF' +
    header.join(',') +
    '\n' +
    data
      .map((bill) => {
        const organizationName = isReceived ? bill.srcOrgName : bill.dstOrgName;
        const issueIdentifier =
          bill.issueIdentifier && bill.issueIdentifier.length > 0
            ? bill.issueIdentifier
            : bill.issueTitle;
        const createdAt = formatDateTime(bill.issueCreatedAt);
        let checkItems = '';
        // TODO Check here
        // where does the tag come from
        const tagCount = JSON.parse(bill.tagCount);
        for (let key in tagCount) {
          const tag = tagCount[key];
          checkItems += `${tag.labelText} * ${tag.count};`;
        }
        const doneAt = formatDateTime(bill.createdAt);
        const originalFee = bill.originalFee;
        const currentFee =
          bill.currentFee === null ? bill.originalFee : bill.currentFee;
        // genereate remark
        let remarks = '';
        const changeHistory = JSON.parse(bill.changeHistory);
        changeHistory.forEach((history) => {
          const historyType = history.type;
          if (historyType === BILL_CHANGE_HISTORY_TYPE.ADJUST) {
            const remark = `${intl.get('billing_csv_price_adjustment')} ${
              history.feeChange
            };`;
            remarks += remark;
          }
        });
        const tagLabels = checkItems.replace(/./g, ' ');
        const remarks_ = remarks.replace(/,/g, ' ');
        return (
          organizationName +
          ',' +
          issueIdentifier +
          ', ' +
          tagLabels +
          ',' +
          createdAt +
          ',' +
          doneAt +
          ',' +
          originalFee +
          ',' +
          currentFee +
          ',' +
          remarks_
        );
      })
      .join('\n');
  return csvContent;
};

export const createDownloadLink = (csvContent, filename) => {
  const encodeUri = encodeURI(csvContent);
  const link = document.createElement('a');
  link.setAttribute('href', encodeUri);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const obtainTagLabelText = (tag) => {
  const languageCode = localData.get(USER_DATA.PREFFERED_LANGUAGE);
  if (tag && Object.prototype.hasOwnProperty.call(tag, 'content')) {
    return tag.content.displayName[languageCode] || tag.content.name;
  } else {
    return tag.labelText;
  }
};
